import * as React from 'react'
import { Trash2 } from 'lucide-react'
import { useCreateOwnOffering, useReplaceOwnOffering } from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import { centsToInput, inputToCents } from '@/lib/price'
import { fileToResizedDataUrl } from '@/lib/image'
import { uploadService, UploadsUnavailableError } from '@/services/UploadService'
import { ImageDropzone } from '@/components/ImageDropzone'
import { OfferingDetailsFields } from '@/components/vendor-profile/OfferingDetailsFields'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ChoiceCard, ChoiceGroup } from '@/components/ui/choice-card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { SegmentedControl, type SegmentedOption } from '@/components/ui/segmented-control'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { FormError } from '@/components/ui/form-error'
import {
  offeringServiceDetails,
  vendorOfferingInputSchema,
  type OfferedService,
  type OfferingServiceDetails,
} from '@lib/input/vendor-offering.input'
import { VENDOR_PRICE_UNITS, VENDOR_PRICE_UNIT_LABELS, type VendorPriceUnit } from '@lib/enums/vendor-price-unit.enum'
import type { VendorOfferingKind } from '@lib/enums/vendor-offering-kind.enum'
import type { VendorPriceVisibility } from '@lib/enums/vendor-price-visibility.enum'
import { MAX_OFFERING_PHOTOS, OFFERING_DESCRIPTION_MAX, OFFERING_NAME_MAX } from '@lib/constants/vendor-profile'
import type { OwnVendorOfferingDto } from '@lib/dto/vendor-profile.dto'

/** A package photo is shown small; there is no need to store a large one. */
const PHOTO_MAX_PX = 1200

const PRICE_VISIBILITY_OPTIONS: readonly SegmentedOption<VendorPriceVisibility>[] = [
  { value: 'shown', label: 'Show the price' },
  { value: 'on_request', label: 'Price on request' },
]

/** A new package's title, written out: lowercasing the labels gave "a av" and "a entertainment". */
const NEW_OFFERING_TITLE: Readonly<Record<OfferedService, string>> = {
  lighting: 'Add a lighting package or item',
  catering: 'Add a catering package or item',
  floral: 'Add a floral package or item',
  photography: 'Add a photography package or item',
  entertainment: 'Add an entertainment package or item',
  av: 'Add an AV package or item',
  planning: 'Add a planning package or item',
  beauty: 'Add a hair and makeup package or item',
  transportation: 'Add a transportation package or item',
}

interface DraftPhoto {
  /** What is sent back: a storage key, or the signed address the API turns back into one. */
  image: string
  /** What is shown. */
  url: string
  caption: string
}

interface Draft {
  kind: VendorOfferingKind
  name: string
  description: string
  priceVisibility: VendorPriceVisibility
  /** Dollars, as typed. */
  price: string
  priceFrom: boolean
  priceUnit: VendorPriceUnit
  serviceDetails: OfferingServiceDetails
  photos: DraftPhoto[]
}

type DraftErrors = Partial<Record<'name' | 'description' | 'priceCents' | 'priceFrom' | 'priceUnit' | 'photos' | 'details', string>>

function isDraftKey(key: PropertyKey | undefined): key is keyof DraftErrors {
  return key === 'name' || key === 'description' || key === 'priceCents' || key === 'priceFrom' || key === 'priceUnit' || key === 'photos' || key === 'details'
}

function draftOf(service: OfferedService, offering: OwnVendorOfferingDto | null): Draft {
  if (offering === null) {
    return {
      kind: 'package',
      name: '',
      description: '',
      priceVisibility: 'shown',
      price: '',
      priceFrom: false,
      priceUnit: 'event',
      // Every field at its default, typed by the service.
      serviceDetails: offeringServiceDetails(service, {}).value,
      photos: [],
    }
  }
  return {
    kind: offering.kind,
    name: offering.name,
    description: offering.description,
    priceVisibility: offering.priceVisibility,
    price: centsToInput(offering.priceCents),
    priceFrom: offering.priceFrom,
    priceUnit: offering.priceUnit,
    // Just the service and its fields: the rest of the stored package is set above.
    serviceDetails: offeringServiceDetails(offering.service, offering.details).value,
    photos: offering.photos.map((photo) => ({ image: photo.url, url: photo.url, caption: photo.caption ?? '' })),
  }
}

interface Props {
  /** The service a new package is for; an existing one keeps its own. */
  service: OfferedService
  /** The package being changed, or null for a new one. */
  offering: OwnVendorOfferingDto | null
  onClose: () => void
}

/**
 * Add or change one package or item: what it is, its price or "price on
 * request", its service's own fields, and up to six photos. Checked with the
 * same schema the server uses before anything is sent.
 */
export function OfferingDialog({ service, offering, onClose }: Props) {
  const create = useCreateOwnOffering()
  const replace = useReplaceOwnOffering()
  const [draft, setDraft] = React.useState<Draft>(() => draftOf(service, offering))
  const [errors, setErrors] = React.useState<DraftErrors>({})
  const [failure, setFailure] = React.useState<string | null>(null)
  const [uploading, setUploading] = React.useState(false)
  const busy = create.isPending || replace.isPending || uploading
  const units = VENDOR_PRICE_UNITS.filter((unit) => unit !== 'table' || service === 'floral')

  function set<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft((current) => ({ ...current, [key]: value }))
  }

  async function addPhotos(files: File[]): Promise<void> {
    setFailure(null)
    setUploading(true)
    try {
      for (const file of files.slice(0, MAX_OFFERING_PHOTOS - draft.photos.length)) {
        const dataUrl = await fileToResizedDataUrl(file, PHOTO_MAX_PX)
        try {
          const { key, url } = await uploadService.uploadImage(dataUrl)
          setDraft((current) => ({ ...current, photos: [...current.photos, { image: key, url, caption: '' }] }))
        } catch (e) {
          if (!(e instanceof UploadsUnavailableError)) throw e
          setDraft((current) => ({ ...current, photos: [...current.photos, { image: dataUrl, url: dataUrl, caption: '' }] }))
        }
      }
    } catch (e) {
      setFailure(requestErrorMessage(e))
    } finally {
      setUploading(false)
    }
  }

  async function save(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setFailure(null)
    const onRequest = draft.priceVisibility === 'on_request'
    const priceCents = onRequest ? null : inputToCents(draft.price)
    if (!onRequest && draft.price.trim() !== '' && priceCents === null) {
      setErrors({ priceCents: 'Enter the price in dollars, like 1,200 or 85.50' })
      return
    }
    const parsed = vendorOfferingInputSchema.safeParse({
      ...draft.serviceDetails,
      kind: draft.kind,
      name: draft.name,
      description: draft.description,
      priceVisibility: draft.priceVisibility,
      priceCents,
      priceFrom: onRequest ? false : draft.priceFrom,
      priceUnit: draft.priceUnit,
      photos: draft.photos.map((photo) => ({ image: photo.image, caption: photo.caption.trim() === '' ? null : photo.caption.trim() })),
    })
    if (!parsed.success) {
      const next: DraftErrors = {}
      for (const issue of parsed.error.issues) {
        const key = issue.path[0]
        if (isDraftKey(key) && next[key] === undefined) next[key] = issue.message
      }
      setErrors(next)
      return
    }
    setErrors({})
    try {
      if (offering === null) await create.mutateAsync(parsed.data)
      else await replace.mutateAsync({ offeringId: offering.id, input: parsed.data })
      onClose()
    } catch (e) {
      setFailure(requestErrorMessage(e))
    }
  }

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{offering === null ? NEW_OFFERING_TITLE[service] : `Change ${offering.name}`}</DialogTitle>
          <DialogDescription>What venue staff and planners see when they look at your profile.</DialogDescription>
        </DialogHeader>
        <form className="space-y-5" onSubmit={(e) => void save(e)} noValidate>
          <ChoiceGroup legend="It’s" columns={2}>
            <ChoiceCard
              type="radio"
              name="offering-kind"
              checked={draft.kind === 'package'}
              onSelect={() => set('kind', 'package')}
              title="A package"
              description="Priced as a whole."
            />
            <ChoiceCard
              type="radio"
              name="offering-kind"
              checked={draft.kind === 'item'}
              onSelect={() => set('kind', 'item')}
              title="An item"
              description="Priced alone."
            />
          </ChoiceGroup>

          <Field label="Name" htmlFor="offering-name" error={errors.name}>
            <Input id="offering-name" value={draft.name} maxLength={OFFERING_NAME_MAX} placeholder="Evening package" onChange={(e) => set('name', e.target.value)} />
          </Field>

          <Field label="What’s included" htmlFor="offering-description" error={errors.description}>
            <Textarea id="offering-description" rows={4} maxLength={OFFERING_DESCRIPTION_MAX} value={draft.description} onChange={(e) => set('description', e.target.value)} />
          </Field>

          <div className="space-y-3">
            <Field label="Price">
              <SegmentedControl
                label="Price"
                value={draft.priceVisibility}
                options={PRICE_VISIBILITY_OPTIONS}
                onValueChange={(visibility) => {
                  if (visibility === 'shown') set('priceVisibility', 'shown')
                  else setDraft((current) => ({ ...current, priceVisibility: 'on_request', price: '', priceFrom: false }))
                }}
              />
            </Field>
            {draft.priceVisibility === 'shown' ? (
              <div className="flex flex-wrap items-end gap-4">
                <Field label="Amount" htmlFor="offering-amount" error={errors.priceCents}>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-ink-muted">$</span>
                    <Input id="offering-amount" inputMode="decimal" value={draft.price} placeholder="1,200" onChange={(e) => set('price', e.target.value)} className="w-32" />
                  </div>
                </Field>
                <Field label="Per" htmlFor="offering-unit" error={errors.priceUnit}>
                  <Select
                    id="offering-unit"
                    value={draft.priceUnit}
                    onChange={(e) => {
                      const unit = units.find((u) => u === e.target.value)
                      if (unit !== undefined) set('priceUnit', unit)
                    }}
                    className="w-36"
                  >
                    {units.map((unit) => (
                      <option key={unit} value={unit}>
                        {VENDOR_PRICE_UNIT_LABELS[unit]}
                      </option>
                    ))}
                  </Select>
                </Field>
                <label className="mb-2 inline-flex items-center gap-2 text-sm">
                  <Checkbox checked={draft.priceFrom} onCheckedChange={(on) => set('priceFrom', on)} />
                  Starting at
                </label>
              </div>
            ) : (
              <p className="text-sm text-ink-muted">No price is shown or kept. Venue staff and planners ask you.</p>
            )}
          </div>

          <div className="space-y-4">
            <OfferingDetailsFields value={draft.serviceDetails} onChange={(serviceDetails) => set('serviceDetails', serviceDetails)} />
            {errors.details ? <FormError>{errors.details}</FormError> : null}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Photos</p>
            {draft.photos.length > 0 ? (
              <ul className="m-0 grid list-none grid-cols-2 gap-3 p-0 sm:grid-cols-3">
                {draft.photos.map((photo, index) => (
                  <li key={photo.image} className="space-y-1.5">
                    <img src={photo.url} alt={photo.caption} className="aspect-[4/3] w-full rounded-md border border-line object-cover" />
                    <div className="flex items-center gap-1">
                      <Input
                        size="sm"
                        value={photo.caption}
                        placeholder="Describe it"
                        aria-label={`Description for photo ${String(index + 1)}`}
                        onChange={(e) => {
                          const caption = e.target.value
                          setDraft((current) => ({ ...current, photos: current.photos.map((p) => (p.image === photo.image ? { ...p, caption } : p)) }))
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove photo ${String(index + 1)}`}
                        onClick={() => setDraft((current) => ({ ...current, photos: current.photos.filter((p) => p.image !== photo.image) }))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
            {draft.photos.length < MAX_OFFERING_PHOTOS ? (
              <ImageDropzone
                onFiles={(files) => void addPhotos(files)}
                multiple
                busy={uploading}
                disabled={busy}
                label="Drag photos here"
                hint={`${String(draft.photos.length)} of ${String(MAX_OFFERING_PHOTOS)}, or click to choose`}
              />
            ) : null}
            {errors.photos ? <FormError>{errors.photos}</FormError> : null}
          </div>

          {failure ? <FormError>{failure}</FormError> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {offering === null ? 'Add' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
